import React, { useRef, useState } from 'react'

import { useConfig } from '@components/config-provider/src'
import { withNativeProps } from '@utils/NativeProps'
import WebUI from '@utils/UpdatePrefixs'
import Rilizhedie from '@tinper/m-icons/lib/cjs/Rilizhedie'
import {
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  getWeeksInMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  add
} from 'date-fns'
import { ArrowLeft } from './ArrowLeft'
import { ArrowLeftDouble } from './ArrowLeftDouble'
import CalendarBody from './CalendarBody'
import { useCalendarContext } from './CalendarContext'
import CalendarHeader from './CalendarHeader'
import { DateRange, Page } from './Convert'
import classNames from 'classnames';

export type DateType = 'month' | 'year';
export type DateAction = 'subtract' | 'add';

const DEFAULT_MAX_ROWS = 6;

export type CalendarRef = {
  jumpTo: (page: Page | ((page: Page) => Page)) => void
  jumpToToday: () => void
}

const defaultProps = {
  weekStartsOn: 'Sunday',
  defaultValue: null as DateRange,
  allowClear: true,
  prevMonthButton: <ArrowLeft />,
  prevYearButton: <ArrowLeftDouble />,
  nextMonthButton: <ArrowLeft />,
  nextYearButton: <ArrowLeftDouble />,
  mode: 'day',
  prependDays: 0,
  maxRows: DEFAULT_MAX_ROWS
}

const CalendarDay = () => {
  const props = useCalendarContext()
  const { classPrefix, current, collapsed, onCollapseChange, showCollapse, prependDays = 0, maxRows = DEFAULT_MAX_ROWS, swipeable, onSwipe, setCurrent, headerClassName, headerExtra, bodyExtra, fieldid } = props

  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const minSwipeDistance = 50

  const maxDay = props.max ? endOfDay(props.max) : null
  const minDay = props.min ? startOfDay(props.min) : null

  const weekStartsOn = props.weekStartsOn === 'Monday' ? 1 : 0
  const start = startOfWeek(startOfMonth(current), { weekStartsOn })
  const end = addWeeks(
    endOfWeek(endOfMonth(current), { weekStartsOn }),
    Math.max((maxRows < 6 ? DEFAULT_MAX_ROWS : maxRows)- getWeeksInMonth(current, { weekStartsOn }), 0)
  )

  const interval = eachDayOfInterval({
    start: maxRows === DEFAULT_MAX_ROWS ? subDays(start, prependDays) : start,
    end
  })

  const handlePageChange = (action: DateAction, num: number, type: DateType) => {
    const nextDate = startOfMonth(
      add(current, {
        years: type === 'year' ? action === 'add' ? num : -num : 0,
        months: type === 'month' ? action === 'add' ? num : -num : 0,
      })
    )
    setCurrent(nextDate)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!swipeable) return
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeable) return
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!swipeable || !touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      handlePageChange('add', 1, 'month')
      onSwipe?.('right')
    }
    if (isRightSwipe) {
      handlePageChange('subtract', 1, 'month')
      onSwipe?.('left')
    }
  }

  const handleCollapseClick = () => {
    onCollapseChange(!collapsed)
  }

  return withNativeProps(
    props,
    <div
      className={classNames(classPrefix, { [`${classPrefix}-collapsed`]: collapsed })}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <CalendarHeader className={headerClassName} />
      {headerExtra && <div className={`${classPrefix}-header-extra`}>{headerExtra}</div>}
      <CalendarMark />
      <CalendarBody
        isRowBeginFn={i => i % 7 == 0}
        isRowEndFn={i => i % 7 == 6}
        minDay={minDay as Date}
        maxDay={maxDay as Date}
        interval={collapsed ? interval.slice(0, 7) : interval}
      />
      {bodyExtra && <div className={`${classPrefix}-body-extra`}>{bodyExtra}</div>}
      {showCollapse && (
        <div
          className={`${classPrefix}-collapse-button`}
          onClick={handleCollapseClick}
          fieldid={`${fieldid}-collapse-btn`}
        >
          <Rilizhedie />
        </div>
      )}
    </div>
  )
}

function CalendarMark() {
  const { classPrefix, weekStartsOn, showWeekNumber } = useCalendarContext()
  const { locale } = useConfig()
  const markItems = [...locale.Calendar.markItems]

  if (weekStartsOn === 'Sunday') {
    const item = markItems.pop()
    if (item) markItems.unshift(item)
  }

  return (
    <div className={classNames(`${classPrefix}-mark`, { [`${classPrefix}-mark-show-week-number`]: showWeekNumber })} >
      {showWeekNumber &&  <div aria-hidden={true} className={`${classPrefix}-mark-cell`}/> }

      {markItems.map((item, index) => (
        <div key={index} className={`${classPrefix}-mark-cell`}>
          {item}
        </div>
      ))}

    </div>
  )
}

export default WebUI({ defaultProps })(CalendarDay)
